import { Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { ToastContainer } from "react-toastify";
import { SkeletonStatCards } from "./components/Skeleton";
import "react-toastify/dist/ReactToastify.css";

// Layouts - loaded immediately as they're shown on every protected route
import UserLayout from "./components/UserLayout";
import AdminLayout from "./components/AdminLayout";
import useAuth from "./context/useAuth";
import Notification from "./components/Notification";

// Route components - lazy loaded
const LoginPage = lazy(() => import("./features/auth/pages/LoginPage"));

// Dashboard
const Homepage = lazy(() => import("./features/Dashboard/page/Homepage"));
const AdminDashboard = lazy(
  () => import("./features/Dashboard/page/AdminDashboard"),
);

// Birthday
const Birthday = lazy(() => import("./features/Birthday/page/BirthdayPage"));

// Employees
const Employee = lazy(() => import("./features/Employees/page/EmployeesPage"));
const AdminEmployeePage = lazy(
  () => import("./features/Employees/page/AdminEmployeePage"),
);
const AddEmp = lazy(
  () => import("./features/Employees/AddEmployee/page/AddEmp"),
);

// Profile
const ProfilePage = lazy(() => import("./features/Profile/page/ProfilePage"));
const AdminProfilePage = lazy(
  () => import("./features/Profile/page/AdminProfilePage"),
);

// Holiday
const HolidayPage = lazy(() => import("./features/Holiday/page/HolidayPage"));
const AdminHolidayPage = lazy(
  () => import("./features/Holiday/page/AdminHolidayPage"),
);
const CreateHolidays = lazy(
  () => import("./features/Holiday/components/CreateHolidays"),
);

// Leave
const LeavePage = lazy(() => import("./features/Leave/page/LeavePage"));
const AdminLeavePage = lazy(
  () => import("./features/Leave/page/AdminLeavePage"),
);

// Attendance
const AttendancePage = lazy(
  () => import("./features/Attendance/page/AttendancePage"),
);
const AdminAttendancePage = lazy(
  () => import("./features/Attendance/page/AdminAttendancePage"),
);

// Other
const CalendarPage = lazy(
  () => import("./features/Calendar/page/CalendarPage"),
);
const AdminAppreciationPage = lazy(
  () => import("./features/Appreciation/page/AdminAppreciationPage"),
);
const PolicyPage = lazy(() => import("./features/Policy/page/PolicyPage"));
const ResignationPage = lazy(
  () => import("./features/Resignation/page/ResignationPage"),
);
const ForwardEncashment = lazy(
  () => import("./features/Encashment/page/ForwardEncashment"),
);
const TaskReport = lazy(() => import("./features/TaskReport/page/taskreport"));

import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./assets/css/UserStyle.css";

import { useEffect } from "react";
import { useUserInfoQuery } from "./features/auth/authApi";
import { useLocation } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import GuestRoute from "./components/GuestRoute";
import { getAccessToken, clearTokens } from "./utils/cookies";
import { clearApiCache } from "./api/cache";

function App() {
  const { user, setUserData, clearUser } = useAuth();
  const location = useLocation();
  const accessToken = getAccessToken();

  const AdminLeavesRoute = () => {
    const role = Number(user?.role);
    return role === 2 ? <LeavePage /> : <AdminLeavePage />;
  };

  const CreateHolidaysRoute = () => {
    const role = Number(user?.role);
    return role === 1 ? <CreateHolidays /> : <AdminHolidayPage />;
  };

  const {
    data: userInfo,
    isError,
    isLoading,
  } = useUserInfoQuery(undefined, {
    skip: !accessToken,
  });

  useEffect(() => {
    const el = document.activeElement;
    if (el && typeof el.blur === "function") {
      el.blur();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!accessToken) {
      clearUser();
    }
  }, [accessToken, clearUser]);

  useEffect(() => {
    if (userInfo?.id && userInfo?.role) {
      setUserData({
        id: userInfo.id,
        role: userInfo.role,
        uuid: userInfo.uuid,
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
      });
    }
    // setUserData is a stable useCallback — intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  useEffect(() => {
    if (!isError) {
      return;
    }

    clearTokens();
    clearApiCache();
    clearUser();
  }, [isError, clearUser]);

  if (
    accessToken &&
    (isLoading ||
      (userInfo?.role != null && Number(user?.role) !== Number(userInfo.role)))
  ) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f6f6f6",
          padding: "88px 24px 32px",
        }}
      >
        <SkeletonStatCards count={4} />
      </div>
    );
  }

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />
      <Routes>
        <Route element={<GuestRoute />}>
          <Route
            path="/"
            element={
              <Suspense
                fallback={
                  <div
                    style={{
                      minHeight: "100vh",
                      background: "#f6f6f6",
                      padding: "88px 24px 32px",
                    }}
                  >
                    <SkeletonStatCards count={4} />
                  </div>
                }
              >
                <LoginPage />
              </Suspense>
            }
          />
          <Route
            path="/login"
            element={
              <Suspense
                fallback={
                  <div
                    style={{
                      minHeight: "100vh",
                      background: "#f6f6f6",
                      padding: "88px 24px 32px",
                    }}
                  >
                    <SkeletonStatCards count={4} />
                  </div>
                }
              >
                <LoginPage />
              </Suspense>
            }
          />
        </Route>

        <Route element={<ProtectedRoute allowedRoles={[3]} />}>
          <Route element={<UserLayout />}>
            <Route
              path="home"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <Homepage />
                </Suspense>
              }
            />
            <Route
              path="birthday"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <Birthday />
                </Suspense>
              }
            />
            <Route
              path="employees"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <Employee />
                </Suspense>
              }
            />
            <Route
              path="holidays"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <HolidayPage />
                </Suspense>
              }
            />
            <Route
              path="calendar"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <CalendarPage />
                </Suspense>
              }
            />
            <Route
              path="profile/:uuid"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <ProfilePage />
                </Suspense>
              }
            />
            <Route
              path="attendance"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <AttendancePage />
                </Suspense>
              }
            />
            <Route
              path="attendance/:id"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <AttendancePage />
                </Suspense>
              }
            />
            <Route
              path="leave"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <LeavePage />
                </Suspense>
              }
            />
            <Route
              path="notification"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <Notification />
                </Suspense>
              }
            />
            <Route
              path="policies"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <PolicyPage />
                </Suspense>
              }
            />
            <Route
              path="resignation"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <ResignationPage />
                </Suspense>
              }
            />
            <Route
              path="encashment"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <ForwardEncashment />
                </Suspense>
              }
            />
            <Route
              path="appreciation"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <AdminAppreciationPage />
                </Suspense>
              }
            />
            <Route
              path="taskreport"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <TaskReport />
                </Suspense>
              }
            />
          </Route>
        </Route>

        <Route element={<ProtectedRoute allowedRoles={[1, 2]} />}>
          <Route path="admin" element={<AdminLayout />}>
            <Route
              path="dashboard"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <AdminDashboard />
                </Suspense>
              }
            />
            <Route
              path="employees"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <AdminEmployeePage />
                </Suspense>
              }
            />
            <Route
              path="profile/:uuid"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <AdminProfilePage />
                </Suspense>
              }
            />
            <Route
              path="employees/add"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <AddEmp />
                </Suspense>
              }
            />
            <Route
              path="leaves"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <AdminLeavesRoute />
                </Suspense>
              }
            />
            <Route
              path="appreciation"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <AdminAppreciationPage />
                </Suspense>
              }
            />
            <Route
              path="holiday"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <AdminHolidayPage />
                </Suspense>
              }
            />
            <Route
              path="create-holidays"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <CreateHolidaysRoute />
                </Suspense>
              }
            />
            <Route
              path="attendance"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <AdminAttendancePage />
                </Suspense>
              }
            />
            <Route
              path="policies"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <PolicyPage />
                </Suspense>
              }
            />
            <Route
              path="resignation"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <ResignationPage />
                </Suspense>
              }
            />
            <Route
              path="encashment"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <ForwardEncashment />
                </Suspense>
              }
            />
            <Route
              path="notification"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <Notification />
                </Suspense>
              }
            />
            <Route
              path="taskreport"
              element={
                <Suspense
                  fallback={
                    <div
                      style={{
                        minHeight: "100vh",
                        background: "#f6f6f6",
                        padding: "88px 24px 32px",
                      }}
                    >
                      <SkeletonStatCards count={4} />
                    </div>
                  }
                >
                  <TaskReport />
                </Suspense>
              }
            />
          </Route>
        </Route>
      </Routes>
    </>
  );
}

export default App;
